import { track, init, setUserId as amplitudeSetUserId } from "@amplitude/analytics-browser"
import { getMetaContent, getMetaContentMaybe } from "./get-meta-content"
import logger from "./logger"
import { hashPhoneNumber } from "./hash-pii"

const RAILS_ENV = getMetaContent<string>("RAILS_ENV")
const AMPLITUDE_API_KEY = getMetaContentMaybe<string>("AMPLITUDE_API_KEY")

export const EVENT_IDS = {
  LOGO: "logo",
  SHARE_SCREEN_SKIP_WAITLIST: "share_screen_skip_waitlist",
  GET_APP_CLICKED: "get_app_clicked",
  DOWNLOAD_APP_STORE_CLICKED: "download_app_store_clicked",
  DOWNLOAD_PLAY_STORE_CLICKED: "download_play_store_clicked",
} as const

export const EVENTS = {
  PHONE_NUMBER_ENTERED: "phone_number_entered",
  CONSENT_GIVEN: "consent_given",
  ITEM_PRESSED: "web_item_pressed",
  SCREEN_VIEWED: "web_screen_viewed",
} as const

export enum SCREENS {
  Landing = "landing",
  Share = "share",
  Download = "download",
}

const BLOCKLIST_PATHS = ["/admin/", "/auth/"]

// Initialize Amplitude with your API key
export const initializeAnalytics = (): void => {
  if (!AMPLITUDE_API_KEY) {
    logger.warn("Amplitude API key not found")
    return
  }

  try {
    init(AMPLITUDE_API_KEY, undefined, {
      logLevel: RAILS_ENV === "production" ? 0 : 2,
      autocapture: {
        sessions: true,
        pageViews: {
          trackOn: () => {
            return !BLOCKLIST_PATHS.some((path) => window.location.pathname.startsWith(path))
          },
        },
        formInteractions: true,
        fileDownloads: true,
      },
    })
  } catch (error) {
    logger.error("Error initializing Amplitude:", error)
  }
}

export const trackEvent = (eventName: string, eventProperties?: Record<string, unknown>): void => {
  const properties = { location: window.location.pathname, ...eventProperties }
  logger.log("Tracking event:", eventName, properties)

  // Send to Amplitude if configured
  if (AMPLITUDE_API_KEY || RAILS_ENV === "development") {
    try {
      track(eventName, properties)
    } catch (error) {
      logger.error("Error tracking event to Amplitude:", error)
    }
  }

  // Send to GTM if whitelisted
  if (shouldSendToGTM(eventName, properties)) {
    sendToGTM(eventName, properties).catch((error) => {
      logger.error("Error sending to GTM:", error)
    })
  }
}

/**
 * Determine if an event should be sent to GTM for ad network pixel tracking
 */
const shouldSendToGTM = (eventName: string, properties: Record<string, unknown>): boolean => {
  // Only track ITEM_PRESSED events
  if (eventName !== EVENTS.ITEM_PRESSED) {
    return false
  }

  // Check if it's one of the phone submission event IDs
  const PHONE_SUBMISSION_EVENT_IDS: readonly string[] = [
    EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
    EVENT_IDS.GET_APP_CLICKED,
  ]

  return PHONE_SUBMISSION_EVENT_IDS.includes(properties.unique_id as string)
}

/**
 * Send event to Google Tag Manager dataLayer
 */
const sendToGTM = async (eventName: string, properties: Record<string, unknown>): Promise<void> => {
  const gtmEventMap: Record<string, string> = {
    [EVENTS.ITEM_PRESSED]: "phone_submit_success",
  }

  const gtmEventName = gtmEventMap[eventName] || eventName
  const { phone_number, attribution_data, ...safeProperties } = properties

  // Hash phone if present (throws on error - caught by caller)
  const phone_number_hash =
    typeof phone_number === "string" ? await hashPhoneNumber(phone_number) : undefined

  // Flatten attribution data for GTM
  const attribution = attribution_data as Record<string, unknown> | undefined
  const flatAttribution: Record<string, unknown> = {}

  if (attribution) {
    // Map click ID to correct parameter name
    if (attribution.clickIdSource && attribution.clickId) {
      const clickIdMap: Record<string, string> = {
        meta: "fbclid",
        tiktok: "ttclid",
        reddit: "rdt_cid",
      }
      const clickIdKey = clickIdMap[attribution.clickIdSource as string]
      if (clickIdKey) flatAttribution[clickIdKey] = attribution.clickId
    }

    // Convert remaining properties from camelCase to snake_case for GTM
    const skipKeys = ["clickId", "clickIdSource"] // Already handled above
    Object.entries(attribution).forEach(([key, value]) => {
      if (!skipKeys.includes(key) && value !== undefined) {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
        flatAttribution[snakeKey] = value
      }
    })
  }

  window.dataLayer?.push({
    event: gtmEventName,
    phone_number_hash,
    ...flatAttribution,
    ...safeProperties,
  })

  logger.log("Sent to GTM:", gtmEventName, {
    event: gtmEventName,
    phone_number_hash,
    ...flatAttribution,
    ...safeProperties,
  })
}

// Set a stable user ID for analytics tracking
export const setAnalyticsUserId = (userId: string): void => {
  logger.log("Setting user ID:", userId)

  if (!AMPLITUDE_API_KEY && RAILS_ENV !== "development") {
    logger.info("Amplitude API key not found; skipping user identification")
    return
  }

  try {
    amplitudeSetUserId(userId)
  } catch (error) {
    logger.error("Error setting user ID:", error)
  }
}
